import { Collapsible as ArkCollapsible } from '@ark-ui/react/collapsible';
import { css, cx } from '@repo/panda/css';
import { collapsible } from '@repo/panda/recipes';
import type { SystemStyleObject } from '@repo/panda/types';
import type React from 'react';
import type { ReactNode } from 'react';
import { forwardRef } from 'react';
import { Icon, type IconBaseProps } from '../ds/icon';
import type { MaterialSymbolName } from '../types/material-symbol-names';

export interface CollapsibleProps {
    defaultOpen?: boolean;
    open?: boolean;
    onOpenChange?: (details: ArkCollapsible.OpenChangeDetails) => void;
    disabled?: boolean;
    trigger: ReactNode;
    children: ReactNode;
    variant?: CollapsibleVariants['variant'];
    size?: CollapsibleVariants['size'];
    className?: SystemStyleObject;
    triggerClassName?: SystemStyleObject;
    /**
     * Inline style applied to the trigger element — use this to override recipe-emitted
     * shorthand properties (e.g. `padding`) that Panda's cascade may not reliably
     * override via `triggerClassName`.
     */
    triggerStyle?: React.CSSProperties;
    contentClassName?: SystemStyleObject;
    showIcon?: boolean;
    icon?: MaterialSymbolName;
    iconProps?: Omit<IconBaseProps, 'icon'>;
}

type CollapsibleVariants = {
    variant: 'dark' | 'light' | 'ghost';
    size: 'sm' | 'md' | 'lg';
};

const iconStyles = css({
    transition: 'transform',
    flexShrink: 0,
    marginLeft: '8',
});

export const Collapsible = forwardRef<HTMLDivElement, CollapsibleProps>(
    (
        {
            defaultOpen,
            open,
            onOpenChange,
            disabled,
            trigger,
            children,
            variant = 'dark',
            size = 'md',
            className,
            triggerClassName,
            triggerStyle,
            contentClassName,
            showIcon = true,
            icon = 'expand_less',
            iconProps,
        },
        ref,
    ) => {
        const styles = collapsible({ variant, size });
        const rootStyle = cx(styles.root, css(className));
        const triggerClass = cx(styles.trigger, css(triggerClassName));
        const contentStyle = cx(styles.content, css(contentClassName));

        return (
            <ArkCollapsible.Root
                ref={ref}
                className={rootStyle}
                defaultOpen={defaultOpen}
                open={open}
                onOpenChange={onOpenChange}
                disabled={disabled}
            >
                <ArkCollapsible.Trigger className={triggerClass} style={triggerStyle}>
                    {trigger}
                    {showIcon && (
                        <Icon
                            icon={icon}
                            size="sm"
                            data-collapsible-icon=""
                            {...iconProps}
                            className={cx(iconStyles, iconProps?.className)}
                            decorative
                        />
                    )}
                </ArkCollapsible.Trigger>
                <ArkCollapsible.Content className={contentStyle}>{children}</ArkCollapsible.Content>
            </ArkCollapsible.Root>
        );
    },
);

Collapsible.displayName = 'Collapsible';
